import React from "react";
import "./App.scss";

import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import EditTemplatePage from "./pages/EditTemplatePage";

import { FirebaseAppProvider, FirestoreProvider, useFirebaseApp } from "reactfire";
import { getFirestore } from "firebase/firestore";
import InternalMappingToolPage from "./pages/CreatorTools/InternalMappingToolPage";
import LineItemDBCreationToolPage from "./pages/CreatorTools/LineItemDBCreationToolPage";
import { firebaseConfig } from "./data/DataStores";
import CreateTestData from "./pages/CreatorTools/MappingTool/CreateTestData";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logDebug } from "./shared/common/logger";
import { XactimateToolPage } from "./pages/CreatorTools/XactimateToolPage";
import VeriskMappingTool, { VeriskMappingView } from "./pages/VeriskMappingTool";
import ModelDetectionPage from "./pages/ModelDetectionPage";
import GeneralPage from "./pages/GeneralPage";
import LineItemOrderPage from "./pages/LineItemOrderPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LoginFlow } from "./components/auth/LoginFlow";
import CustomReesePromptsPage from "./pages/CustomReesePromptsPage";
import ModuleConfigurationPage from "./pages/ModuleConfigurationPage";
import RulesEnginePage from "./pages/RulesEnginePage";
import TPAsCarriersPage from "./pages/TPAsCarriersPage";
import EstimateExportHeadersPage from "./pages/EstimateExportHeadersPage";
import MitToReconPage from "./pages/MitToReconPage";
import DeployDashboardPage from "./pages/deploy/DeployDashboardPage";
import ResponsiveNoteTemplatesPage from "./pages/responsive-note-templates/ResponsiveNoteTemplatesPage";
import ProcessorConfigPage from "./pages/processor-config/ProcessorConfigPage";
import RateCardsPage from "./pages/rate-cards/RateCardsPage";
import CustomContextPage from "./pages/custom-context/CustomContextPage";
import ManualEstimateReviewsPage from "./pages/manual-estimate-reviews/ManualEstimateReviewsPage";

const AuthenticatedApp: React.FC = () => {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginFlow />;
  }

  return <ToolsApp />;
};

const ToolsApp: React.FC = () => {
  const firestoreInstance = getFirestore(useFirebaseApp());
  return (
    <FirestoreProvider sdk={firestoreInstance}>
      <div className="m-8" id="all-parents">
        <Router>
          <Routes>
            <Route path="/verisk-mapping-view" element={<VeriskMappingView />} />
            <Route path="/verisk-mapping-tool" element={<VeriskMappingTool />} />
            <Route path="/template" element={<EditTemplatePage />} />
            <Route path="/mapping-internal" element={<InternalMappingToolPage />} />
            <Route path="/xactimate" element={<XactimateToolPage />} />
            <Route path="/create-test-data" element={<CreateTestData />} />
            <Route path="/tools" element={<LineItemDBCreationToolPage />} />
            <Route path="/model-detection" element={<ModelDetectionPage />} />
            <Route path="/general" element={<GeneralPage />} />
            <Route path="/line-item-order" element={<LineItemOrderPage />} />
            <Route path="/custom-reese-prompts" element={<CustomReesePromptsPage />} />
            <Route path="/modules" element={<ModuleConfigurationPage />} />
            <Route path="/responsive-note-templates" element={<ResponsiveNoteTemplatesPage />} />
            <Route path="/rules-engine" element={<RulesEnginePage />} />
            <Route path="/tpas-carriers" element={<TPAsCarriersPage />} />
            <Route path="/estimate-export-headers" element={<EstimateExportHeadersPage />} />
            <Route path="/mit-to-recon" element={<MitToReconPage />} />
            <Route path="/processor-config" element={<ProcessorConfigPage />} />
            <Route path="/rate-cards" element={<RateCardsPage />} />
            <Route path="/custom-context" element={<CustomContextPage />} />
            <Route path="/manual-estimate-reviews" element={<ManualEstimateReviewsPage />} />
            <Route path="/deploy" element={<DeployDashboardPage />} />
            <Route path="*" element={<LineItemDBCreationToolPage />} />
          </Routes>
        </Router>
      </div>
      <ToastContainer
        toastClassName="w-[450px] left-[-50%]"
        position="top-right"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        theme="light"
      />
    </FirestoreProvider>
  );
};

const AppWrapper: React.FC = () => {
  return (
    <FirebaseAppProvider firebaseConfig={firebaseConfig}>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </FirebaseAppProvider>
  );
};

// export default App;
export default AppWrapper;
